import React from 'react';
import { Conta, TipoContaId } from '../../types/financeiro';
import { sanitizeText } from '../../utils/textSanitizer';
import { ProtectedField, MaskedValue } from '../common/ProtectedField';
import { 
  Eye, 
  Edit, 
  Trash2, 
  CheckCircle2, 
  FileText, 
  Receipt, 
  Calendar, 
  AlertCircle, 
  Clock, 
  ExternalLink,
  Landmark,
  Building2 
} from 'lucide-react';

export interface FinanceiroContaRowProps {
  key?: React.Key;
  conta: Conta;
  pessoaNome: string;
  isReceita: boolean;
  onView: (c: Conta) => void;
  onEdit: (c: Conta) => void;
  onDelete: (c: Conta) => void;
  onBaixa: (c: Conta) => void;
  onViewComprovante: (c: Conta) => void;
  onEmitirNfse?: (c: Conta) => void;
}

export function FinanceiroContaRow({
  conta,
  pessoaNome,
  isReceita,
  onView,
  onEdit,
  onDelete,
  onBaixa,
  onViewComprovante,
  onEmitirNfse
}: FinanceiroContaRowProps) {
  const getStatus = (): 'atrasada' | 'aberta' | 'quitada' => {
    if (conta.dt_pagamento) return 'quitada';
    if (!conta.dt_vencimento) return 'aberta';
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const vencimento = new Date(conta.dt_vencimento + 'T00:00:00');
    return vencimento < today ? 'atrasada' : 'aberta';
  };

  const status = getStatus();

  function formatDateBR(dateStr?: string | null): string {
    if (!dateStr) return '-';
    const parts = dateStr.split('T')[0].split('-');
    if (parts.length === 3) {
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return dateStr;
  }

  const formatMoney = (val: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);

  const hasComprovante = Boolean(
    conta.comprovante_url || 
    conta.boleto_url || 
    conta.nf_url || 
    (conta.obs && conta.obs.includes('COMPROVANTE_URL:'))
  );

  return (
    <tr className="hover:bg-slate-50/80 transition-colors group">
      {/* ID */}
      <td className="px-4 py-3.5 text-xs font-bold text-slate-700 whitespace-nowrap">
        #{conta.id}
      </td>

      {/* Descrição & NF */}
      <td className="px-4 py-3.5 text-sm text-slate-900 font-semibold min-w-[200px]">
        <div className="flex flex-col">
          <span className="truncate" title={sanitizeText(conta.descricao) || ''}>
            {sanitizeText(conta.descricao) || 'Sem descrição'}
          </span>
          <div className="flex flex-wrap items-center gap-1.5 mt-1">
            {conta.numero_nf ? (
              <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-800 bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-200" title={`NFS-e emitida: ${conta.numero_nf}`}>
                <Receipt className="w-3 h-3 text-emerald-600" />
                NFS-e: #{sanitizeText(conta.numero_nf)}
              </span>
            ) : isReceita && onEmitirNfse ? (
              <button
                type="button"
                id={`btn-gerar-nfse-inline-${conta.id}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onEmitirNfse(conta);
                }}
                className="inline-flex items-center gap-1 text-[11px] font-bold text-purple-700 bg-purple-50 hover:bg-purple-100 px-2 py-0.5 rounded-md border border-purple-200 transition-colors cursor-pointer shadow-2xs"
                title="Emitir Nota Fiscal de Serviços (NFS-e) para este faturamento"
              >
                <Receipt className="w-3 h-3 text-purple-600" />
                <span>Gerar NFS-e</span>
              </button>
            ) : null}
            {conta.banco_nome ? (
              <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded border border-blue-200">
                <Landmark className="w-3 h-3 text-blue-500" />
                {sanitizeText(conta.banco_nome)}
              </span>
            ) : null}
            {conta.cnpj_faturador ? (
              <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-700 bg-indigo-50 px-1.5 py-0.5 rounded border border-indigo-200" title={`Faturado por: ${conta.cnpj_faturador}`}>
                <Building2 className="w-3 h-3 text-indigo-500" />
                {conta.cnpj_faturador.includes('Voope Two') ? 'Voope Two' : (conta.cnpj_faturador.includes('23.059') ? 'Voope Matriz' : conta.cnpj_faturador)}
              </span>
            ) : null}
            {conta.parcela && conta.parcela > 1 && (
              <span className="text-[10px] font-semibold text-slate-500">
                Parc. {conta.parcela}
              </span>
            )}
          </div>
        </div>
      </td>

      {/* Favorecido / Cliente */}
      <td className="px-4 py-3.5 text-xs text-slate-700 font-medium max-w-[180px] truncate" title={sanitizeText(pessoaNome)}>
        {sanitizeText(pessoaNome)}
      </td>

      {/* Vencimento */}
      <td className="px-4 py-3.5 text-xs text-slate-600 whitespace-nowrap font-medium">
        {formatDateBR(conta.dt_vencimento)}
      </td>

      {/* Pagamento / Baixa */}
      <td className="px-4 py-3.5 text-xs whitespace-nowrap">
        {conta.dt_pagamento ? (
          <span className="inline-flex items-center gap-1 text-emerald-700 font-bold bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-200">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
            {formatDateBR(conta.dt_pagamento)}
          </span>
        ) : (
          <span className="text-slate-400 italic text-[11px]">Pendente</span>
        )}
      </td>

      {/* Valor Protegido */}
      <td className="px-4 py-3.5 text-xs font-extrabold whitespace-nowrap">
        <MaskedValue
          permission="view_financial_values"
          value={conta.valor}
          type="currency"
          className={isReceita ? 'text-emerald-600' : 'text-rose-600'}
        />
        {conta.valor_pago && conta.valor_pago !== conta.valor && (
          <div className="text-[10px] text-slate-500 font-normal mt-0.5">
            Pago:{' '}
            <MaskedValue
              permission="view_financial_values"
              value={conta.valor_pago}
              type="currency"
            />
          </div>
        )}
      </td>

      {/* Comprovantes & Anexos */}
      <td className="px-4 py-3.5 text-center whitespace-nowrap">
        {hasComprovante ? (
          <button
            type="button"
            onClick={() => onViewComprovante(conta)}
            title="Ver Documentos e Anexos"
            className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 rounded-lg text-[11px] font-bold transition-colors cursor-pointer"
          >
            <FileText className="w-3.5 h-3.5" />
            Anexos
          </button>
        ) : (
          <span className="text-[11px] text-slate-300">-</span>
        )}
      </td>

      {/* Status */}
      <td className="px-4 py-3.5 text-xs whitespace-nowrap">
        {status === 'quitada' ? (
          <span className="bg-emerald-600 text-white px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider shadow-2xs">
            {isReceita ? 'Recebida' : 'Quitada'}
          </span>
        ) : status === 'atrasada' ? (
          <span className="bg-rose-600 text-white px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider shadow-2xs">
            Atrasada
          </span>
        ) : (
          <span className="bg-amber-500 text-white px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider shadow-2xs">
            Aberta
          </span>
        )}
      </td>

      {/* Ações */}
      <td className="px-4 py-3.5 text-right whitespace-nowrap text-xs font-medium">
        <div className="flex items-center justify-end gap-1">
          {/* Botão Baixar / Quitar protegido com pay_financial */}
          <ProtectedField permission="pay_financial">
            <button
              type="button"
              onClick={() => onBaixa(conta)}
              title={conta.dt_pagamento ? 'Ver/Alterar Baixa' : isReceita ? 'Confirmar Recebimento' : 'Dar Baixa no Pagamento'}
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-bold transition-all shadow-2xs cursor-pointer ${
                conta.dt_pagamento
                  ? 'bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-200'
                  : isReceita
                  ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                  : 'bg-rose-600 text-white hover:bg-rose-700'
              }`}
            >
              <CheckCircle2 className="w-3.5 h-3.5" />
              <span>{conta.dt_pagamento ? 'Baixada' : 'Dar Baixa'}</span>
            </button>
          </ProtectedField>

          {/* Visualizar */}
          <button
            type="button"
            onClick={() => onView(conta)}
            title="Visualizar Detalhes"
            className="p-1.5 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer"
          >
            <Eye className="w-4 h-4" />
          </button>

          {/* Botão Emitir NFS-e para Contas a Receber */}
          {isReceita && onEmitirNfse && (
            <ProtectedField permission="manage_financial">
              <button
                type="button"
                id={`btn-nfse-conta-${conta.id}`}
                onClick={() => onEmitirNfse(conta)}
                title={conta.numero_nf ? `NFS-e #${conta.numero_nf} já emitida (Clique para gerenciar/reemitir)` : "Emitir Nota Fiscal de Serviços (NFS-e)"}
                className={`p-1.5 rounded-lg transition-colors cursor-pointer ${
                  conta.numero_nf
                    ? 'text-emerald-700 hover:text-emerald-900 hover:bg-emerald-50'
                    : 'text-purple-600 hover:text-purple-800 hover:bg-purple-50'
                }`}
              >
                <Receipt className="w-4 h-4" />
              </button>
            </ProtectedField>
          )}

          {/* Editar protegido com manage_financial */}
          <ProtectedField permission="manage_financial">
            <button
              type="button"
              onClick={() => onEdit(conta)}
              title="Editar Conta"
              className="p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors cursor-pointer"
            >
              <Edit className="w-4 h-4" />
            </button>
          </ProtectedField>

          {/* Excluir protegido com delete_financial */}
          <ProtectedField permission="delete_financial">
            <button
              type="button"
              onClick={() => onDelete(conta)}
              title="Excluir Conta"
              className="p-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </ProtectedField>
        </div>
      </td>
    </tr>
  );
}
